import { config } from "@/config";

const TOKEN_KEY = "nuforce_admin_token";

export function setAdminToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getAdminToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Called once when the API says the caller is not authenticated.
 *
 * The admin JWT lives 30 minutes and SessionGate only validates on mount, so
 * an operator who left a tab open came back to every panel reading "Could not
 * load…" with their own name still in the sidebar — the portal looked broken
 * rather than logged out. Registered by SessionGate, which owns navigation.
 *
 * 401 only. A 403 means authenticated but lacking an ability, and signing
 * someone out for opening a page they cannot read would be its own bug.
 */
let onUnauthorized: (() => void) | null = null;
let notified = false;

export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
  notified = false;
}

function handleUnauthorized() {
  // A dashboard fires several calls at once; all of them 401 together and the
  // operator only needs telling once.
  if (notified) return;
  notified = true;
  setAdminToken(null);
  onUnauthorized?.();
}

export class HttpError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
  }
}

export async function http<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  const token = getAdminToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.json !== undefined) {
    headers.set("Content-Type", "application/json");
    init.body = JSON.stringify(init.json);
  }
  headers.set("Accept", "application/json");
  // Intentionally NOT sending credentials. The admin portal authenticates
  // exclusively via the admin-scoped JWT in the Authorization header above —
  // this prevents any cookie set at the parent .nuforce360.com domain from
  // hitchhiking on admin requests.
  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    ...init,
    headers,
    credentials: "omit",
  });
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    if (res.status === 401) handleUnauthorized();
    throw new HttpError(res.status, `HTTP ${res.status}`, body);
  }
  return body as T;
}

/**
 * A `/action/*` call, which does not use HTTP status to report failure.
 *
 * The action handlers answer `200 OK` with `{ success: false, error: "Admin
 * access required" }` in the body. Plain `http()` only throws on a non-2xx, so
 * every one of those landed in react-query's `onSuccess` and the UI cheerfully
 * reported "Task deleted." for a request the server had refused. Seventeen
 * call sites across the AI and feature-flag modules were reading failures as
 * successes.
 *
 * `success` is optional in the envelope, so only an explicit `false` is a
 * failure — a handler that omits the field is not silently treated as broken.
 */
export async function action<T extends { success?: boolean; error?: string | null }>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const body = await http<T>(path, init);
  if (body && body.success === false) {
    throw new HttpError(200, body.error || "The server rejected this action.", body);
  }
  return body;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
